'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceDelegationReceipt } from '@/components/org-chat/voice-call-bar-view';
import { normalizeVoiceDelegationReceiptPrompt } from '@/components/org-chat/voice-call-tool-input';

const RECEIPT_TTL_MS = 20_000;
const STARTED_RECEIPT_TTL_MS = 5_000;
const FAILED_RECEIPT_TTL_MS = 8_000;

type QueuedVoiceMessage = { prompt?: string };

export function useVoiceDelegationReceipts(
  open: boolean,
  queuedMessages: ReadonlyArray<QueuedVoiceMessage> | undefined
) {
  const [receipts, setReceipts] = useState<Array<VoiceDelegationReceipt>>([]);
  const sequenceRef = useRef(0);

  const addReceipt = useCallback(
    (args: { prompt: string; mode: 'queue' | 'interrupt' }) => {
      const normalizedPrompt = normalizeVoiceDelegationReceiptPrompt(
        args.prompt
      );
      const now = Date.now();
      const id = `voice-delegation-${now}-${sequenceRef.current++}`;
      setReceipts((current) => {
        const withoutDuplicate = current.filter(
          (receipt) =>
            receipt.normalizedPrompt !== normalizedPrompt ||
            receipt.status === 'failed'
        );
        return [
          ...withoutDuplicate,
          {
            id,
            prompt: normalizedPrompt,
            normalizedPrompt,
            mode: args.mode,
            status: 'sending' as const,
            createdAt: now,
            updatedAt: now,
          },
        ].slice(-3);
      });
      return id;
    },
    []
  );

  const updateReceipt = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<VoiceDelegationReceipt, 'mode' | 'status' | 'updatedAt'>
      >
    ) => {
      setReceipts((current) =>
        current.map((receipt) =>
          receipt.id === id
            ? { ...receipt, ...patch, updatedAt: patch.updatedAt ?? Date.now() }
            : receipt
        )
      );
    },
    []
  );

  useEffect(() => {
    if (!open) return;

    const timer = setInterval(() => {
      const now = Date.now();
      setReceipts((current) =>
        current.filter((receipt) => {
          const ttl =
            receipt.status === 'failed'
              ? FAILED_RECEIPT_TTL_MS
              : receipt.status === 'started'
                ? STARTED_RECEIPT_TTL_MS
                : RECEIPT_TTL_MS;
          return now - receipt.updatedAt < ttl;
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  useEffect(() => {
    const queuedPrompts = new Set(
      (queuedMessages ?? [])
        .map((item) => normalizeVoiceDelegationReceiptPrompt(item.prompt ?? ''))
        .filter(Boolean)
    );
    if (queuedPrompts.size === 0) return;

    const timer = setTimeout(() => {
      setReceipts((current) =>
        current.filter(
          (receipt) =>
            receipt.status === 'started' ||
            !queuedPrompts.has(receipt.normalizedPrompt)
        )
      );
    }, 0);
    return () => clearTimeout(timer);
  }, [queuedMessages]);

  const clearReceipts = useCallback(() => setReceipts([]), []);

  return {
    receipts: open ? receipts : [],
    addReceipt,
    updateReceipt,
    clearReceipts,
  };
}
